<template>
  <main class="min-h-screen bg-white">
    <!-- Hero Section - Our Impact / Our Work -->
    <section class="relative min-h-screen flex items-center overflow-hidden">
      <!-- Background Image with Overlay -->
      <div class="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=1920&h=1080&fit=crop"
          alt="Community impact"
          class="w-full h-full object-cover"
        />
        <div
          class="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/80"
        ></div>
      </div>

      <!-- Content -->
      <div class="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div class="max-w-3xl text-center mt-4">
          <!-- <span
            class="inline-block bg-yellow text-black px-4 py-1 rounded-full text-sm font-semibold mb-6"
          >
            Our Impact
          </span> -->

          <h1
            class="text-5xl lg:text-7xl font-black text-white leading-tight mb-6"
          >
            Changing Lives, One Story at a Time
          </h1>

          <p class="text-xl text-gray-200 mb-10 leading-relaxed">
            From rural communities to bustling cities, our work has empowered
            over
            <span class="font-bold text-yellow-400">10,000+ individuals</span>
            with education, skills, and opportunities to create lasting change.
          </p>
        </div>
      </div>
    </section>

    <!-- Floating Stats -->
    <section class="relative -mt-20 z-20">
      <div class="max-w-6xl mx-auto px-6">
        <div class="bg-white rounded-3xl shadow-2xl p-8">
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div class="text-center">
              <div class="text-4xl font-black text-blue-600 mb-2">$5M+</div>
              <div class="text-gray-600 font-medium">Awarded</div>
            </div>
            <div class="text-center">
              <div class="text-4xl font-black text-green-600 mb-2">750+</div>
              <div class="text-gray-600 font-medium">Recipients</div>
            </div>
            <div class="text-center">
              <div class="text-4xl font-black text-purple-600 mb-2">98%</div>
              <div class="text-gray-600 font-medium">Success Rate</div>
            </div>
            <div class="text-center">
              <div class="text-4xl font-black text-orange-600 mb-2">20+</div>
              <div class="text-gray-600 font-medium">Years</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Programs Carousel -->
    <section class="py-24 bg-gray-50">
      <div class="max-w-7xl mx-auto px-6">
        <div class="text-center mb-16">
          <h2 class="text-5xl font-black text-gray-900 mb-6">Find Your Path</h2>
          <p class="text-xl text-gray-600">
            Three life-changing opportunities await
          </p>
        </div>

        <div class="relative">
          <div class="grid lg:grid-cols-3 gap-8">
            <div
              v-for="program in programs"
              :key="program.id"
              class="group relative overflow-hidden rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
            >
              <div class="absolute inset-0">
                <img
                  :src="program.image"
                  :alt="program.title"
                  class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div
                  class="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"
                ></div>
              </div>

              <div
                class="relative z-10 p-8 h-96 flex flex-col justify-end text-white"
              >
                <div class="mb-4">
                  <span
                    :class="program.badge"
                    class="inline-block px-3 py-1 rounded-full text-sm font-bold mb-4"
                  >
                    {{ program.category }}
                  </span>
                </div>
                <h3 class="text-3xl font-bold mb-4">{{ program.title }}</h3>
                <p class="text-gray-200 mb-6 leading-relaxed">
                  {{ program.description }}
                </p>
                <div class="flex justify-between items-center">
                  <div>
                    <span class="text-2xl font-bold text-yellow-400">{{
                      program.amount
                    }}</span>
                    <div class="text-sm text-gray-300">
                      {{ program.duration }}
                    </div>
                  </div>
                  <button
                    class="bg-white text-black hover:bg-yellow-400 font-bold py-2 px-6 rounded-full transition-all duration-300 transform group-hover:scale-105"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Success Stories Showcase -->
    <section class="py-24 bg-white">
      <div class="max-w-7xl mx-auto px-6">
        <div class="text-center mb-16">
          <h2 class="text-5xl font-black text-gray-900 mb-6">
            Stories of Transformation
          </h2>
          <p class="text-xl text-gray-600">
            Real people. Real change. Real impact.
          </p>
        </div>

        <div class="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div class="relative">
            <img
              src="https://images.unsplash.com/photo-1544717297-fa95b6ee9643?w=600&h=700&fit=crop&crop=face"
              alt="Maria's story"
              class="w-full h-[500px] object-cover rounded-3xl shadow-2xl"
            />
            <div
              class="absolute -bottom-6 -right-6 bg-green-500 text-white p-4 rounded-2xl shadow-xl"
            >
              <div class="text-2xl font-bold">2019</div>
              <div class="text-sm">Graduate</div>
            </div>
          </div>

          <div>
            <div
              class="bg-blue-50 inline-block px-4 py-2 rounded-full text-blue-600 font-semibold mb-6"
            >
              Community Impact
            </div>
            <h3 class="text-4xl font-bold text-gray-900 mb-6">
              From Refugee to Doctor
            </h3>
            <p class="text-xl text-gray-600 mb-8 leading-relaxed">
              "When I arrived as a refugee with nothing, Mshel Foundation didn't
              just give me a scholarship—they gave me hope. Today, I'm treating
              patients in the same community that welcomed me."
            </p>
            <div class="space-y-4 mb-8">
              <div class="flex items-center">
                <span
                  class="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 text-white text-sm"
                  >✓</span
                >
                <span
                  ><strong>Dr. Maria Santos</strong> - First in family to attend
                  university</span
                >
              </div>
              <div class="flex items-center">
                <span
                  class="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 text-white text-sm"
                  >✓</span
                >
                <span
                  >Now serves <strong>500+ patients monthly</strong> in
                  underserved areas</span
                >
              </div>
              <div class="flex items-center">
                <span
                  class="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 text-white text-sm"
                  >✓</span
                >
                <span
                  >Mentors
                  <strong>12 current scholarship recipients</strong></span
                >
              </div>
            </div>
            <button
              class="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-xl transition-colors duration-300"
            >
              Read Full Story
            </button>
          </div>
        </div>

        <!-- Story Grid -->
        <div class="grid md:grid-cols-3 gap-8">
          <div
            v-for="story in quickStories"
            :key="story.id"
            class="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl border hover:shadow-lg transition-all duration-300"
          >
            <div class="flex items-center mb-4">
              <img
                :src="story.image"
                :alt="story.name"
                class="w-12 h-12 rounded-full mr-4 object-cover"
              />
              <div>
                <h4 class="font-bold text-gray-900">{{ story.name }}</h4>
                <p class="text-sm text-gray-600">{{ story.role }}</p>
              </div>
            </div>
            <p class="text-gray-700 italic mb-4">"{{ story.quote }}"</p>
            <div class="text-blue-600 font-semibold text-sm">
              {{ story.impact }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Future Goals -->
    <section class="py-24 bg-white">
      <div class="max-w-7xl mx-auto px-6">
        <div class="text-center mb-16">
          <h2 class="text-5xl font-black text-gray-900 mb-6">What's Next</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Our ambitious roadmap for expanding impact over the next decade
          </p>
        </div>

        <div class="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div class="space-y-8">
              <div v-for="goal in futureGoals" :key="goal.id" class="relative">
                <div class="flex items-start space-x-6">
                  <div
                    :class="goal.iconBg"
                    class="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg"
                  >
                    <span class="text-2xl">{{ goal.icon }}</span>
                  </div>
                  <div class="flex-1">
                    <div class="flex items-center mb-3">
                      <h3 class="text-2xl font-bold text-gray-900 mr-4">
                        {{ goal.title }}
                      </h3>
                      <span
                        class="bg-green-100 text-green-600 px-3 py-1 rounded-full text-sm font-semibold"
                        >{{ goal.timeline }}</span
                      >
                    </div>
                    <p class="text-gray-600 mb-4 leading-relaxed">
                      {{ goal.description }}
                    </p>
                    <div class="flex items-center text-blue-600 font-semibold">
                      <span class="mr-2">Target:</span>
                      <span class="text-2xl font-bold">{{ goal.target }}</span>
                    </div>
                  </div>
                </div>
                <div
                  v-if="goal.id !== futureGoals.length"
                  class="absolute left-8 top-16 w-px h-16 bg-gray-200"
                ></div>
              </div>
            </div>
          </div>

          <div class="relative">
            <img
              src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=800&fit=crop"
              alt="Future vision"
              class="w-full h-[600px] object-cover rounded-3xl shadow-2xl"
            />
            <div
              class="absolute inset-0 bg-gradient-to-t from-blue-900 via-transparent to-transparent opacity-50 rounded-3xl"
            ></div>
            <div class="absolute bottom-8 left-8 right-8 text-white">
              <h4 class="text-3xl font-bold mb-4">2030 Vision</h4>
              <p class="text-lg leading-relaxed">
                Reaching 1 million students globally with comprehensive
                educational support and community development programs.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section
      class="py-24 bg-gradient-to-br from-gray-900 to-black text-white relative overflow-hidden"
    >
      <div class="absolute inset-0 opacity-5">
        <img
          src="https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=1920&h=1080&fit=crop"
          alt="Students studying"
          class="w-full h-full object-cover"
        />
      </div>

      <div class="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <h2 class="text-6xl font-black mb-8 leading-tight">
          Don't Wait.
          <span
            class="text-transparent bg-clip-text bg-gradient-to-r from-yellow to-orange-500"
          >
            Apply Today.
          </span>
        </h2>
        <p class="text-2xl text-gray-300 mb-12 leading-relaxed">
          Applications close March 15th. Join the next cohort of scholarship
          recipients.
        </p>

        <div
          class="flex flex-col sm:flex-row gap-6 justify-center items-center"
        >
          <button
            class="bg-yellow hover:from-yellow-500 hover:to-orange-600 text-white font-bold py-4 px-12 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-xl"
          >
            Start Application
          </button>
          <div class="flex items-center text-gray-300">
            <span class="mr-2">Questions?</span>
            <a
              href="mailto:help@mshelfoundation.org"
              class="text-yellow-400 hover:text-yellow-300 transition-colors duration-300 font-semibold"
            >
              help@mshelfoundation.org
            </a>
          </div>
        </div>

        <div
          class="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl mx-auto"
        >
          <div class="flex items-center justify-center">
            <span class="text-green-400 mr-2">✓</span>
            <span class="text-sm">No application fee</span>
          </div>
          <div class="flex items-center justify-center">
            <span class="text-green-400 mr-2">✓</span>
            <span class="text-sm">5-minute application</span>
          </div>
          <div class="flex items-center justify-center">
            <span class="text-green-400 mr-2">✓</span>
            <span class="text-sm">Results in 2 weeks</span>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
export default {
  name: "ScholarshipPage",
  data() {
    return {
      programs: [
        {
          id: 1,
          title: "Future Leaders",
          category: "LEADERSHIP",
          description:
            "For visionaries who want to change the world through innovation and leadership.",
          amount: "$15,000",
          duration: "4 years renewable",
          image:
            "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop",
          badge: "bg-blue-500 text-white",
        },
        {
          id: 2,
          title: "STEM Pioneers",
          category: "INNOVATION",
          description:
            "Advancing tomorrow's technology through exceptional STEM students.",
          amount: "$20,000",
          duration: "4 years renewable",
          image:
            "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=600&h=400&fit=crop",
          badge: "bg-green-500 text-white",
        },
        {
          id: 3,
          title: "Community Champions",
          category: "SERVICE",
          description:
            "Empowering students who are already making a difference in their communities.",
          amount: "$10,000",
          duration: "2 years renewable",
          image:
            "https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=600&h=400&fit=crop",
          badge: "bg-purple-500 text-white",
        },
      ],
      achievements: [
        { id: 1, icon: "🎓", number: "98%", label: "Graduate on Time" },
        { id: 2, icon: "💼", number: "85%", label: "Employed in Field" },
        { id: 3, icon: "🌟", number: "4.2", label: "Average GPA" },
        { id: 4, icon: "🏆", number: "50+", label: "Awards Won" },
      ],
      quickStories: [
        {
          id: 1,
          name: "David Kim",
          role: "Software Engineer",
          quote: "The scholarship opened doors I never knew existed.",
          impact: "Now earning 5x his family income",
          image:
            "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
        },
        {
          id: 2,
          name: "Fatima Al-Zahra",
          role: "Teacher & Activist",
          quote: "Education gave me the power to change my community.",
          impact: "Founded 3 schools in rural areas",
          image:
            "https://images.unsplash.com/photo-1494790108755-2616b332643f?w=100&h=100&fit=crop&crop=face",
        },
        {
          id: 3,
          name: "Carlos Mendez",
          role: "Doctor",
          quote: "From migrant worker's son to pediatric surgeon.",
          impact: "Serves 200+ children monthly",
          image:
            "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=100&h=100&fit=crop&crop=face",
        },
      ],
      futureGoals: [
        {
          id: 1,
          title: "Scale to 1M Students",
          description:
            "Expand our reach to support one million students globally through direct scholarships and educational infrastructure.",
          target: "1,000,000",
          timeline: "2030",
          icon: "🎯",
          iconBg: "bg-gradient-to-r from-blue-500 to-indigo-600 text-white",
        },
        {
          id: 2,
          title: "Build 500 Schools",
          description:
            "Construct and equip modern educational facilities in underserved communities worldwide.",
          target: "500 Schools",
          timeline: "2028",
          icon: "🏫",
          iconBg: "bg-gradient-to-r from-green-500 to-emerald-600 text-white",
        },
        {
          id: 3,
          title: "Digital Learning Platform",
          description:
            "Launch comprehensive online education platform accessible to students in remote areas.",
          target: "50 Languages",
          timeline: "2025",
          icon: "💻",
          iconBg: "bg-gradient-to-r from-purple-500 to-violet-600 text-white",
        },
      ],
    };
  },
  methods: {
    openApplication() {
      // Handle application opening
      console.log("Opening application...");
    },
    watchVideo() {
      // Handle video watching
      console.log("Playing success stories video...");
    },
  },
};
</script>

<style scoped>
.text-shadow {
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
}
</style>
